import { useState, useEffect, useRef, useContext, useCallback, memo, Fragment, useReducer } from 'react';
import { useMediaQuery } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import Box from '@mui/material/Box';
import Breakout from './Breakout.js';
import Button from '@mui/material/Button';
import CancelIcon from '@mui/icons-material/Cancel';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FormHelperText from '@mui/material/FormHelperText';
import { blue, red } from '@mui/material/colors';
import Grid from '@mui/material/GridLegacy';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { LineChart } from '@mui/x-charts/LineChart';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import Typography from '@mui/material/Typography';
import { UserContext, FetchContext, NestingContext, TaskContext } from './context.js';
import { Viewer } from './Viewer.js';
import { viewerState } from './viewerState.js';
import { useNavigate, useLoaderData } from 'react-router-dom';
import { style } from './style.js';
import { Quaternion, Euler } from 'three';
import usePersistedToggle from './useOnce.js';
import useLocalStorage from "./storage.js";

function fieldSetter(item, setItem, field){
	return (value) => {
		let newItem = {...item};
		newItem[field] = value;
		setItem(newItem);
	}
}

const axisNames = [
	"X min",
	"Y min",
	"Z min",
	"X max",
	"Y max",
	"Z max"
]

const numberInputSx = {minWidth: "80px"};

function BoxField({box, setBox, prefix="", sx={}}){
	function arraySetter(idx){
		return (evt) => {
			setBox(box.map((val, i) => {
				if(i === idx){
					return evt.target.value;
				}else{
					return val;
				}
			}));
		}
	}
  let sxAll = {...numberInputSx, flex: "1 1 auto", m: 0.5};
  let sxFirst = {...sxAll, marginLeft: 0};
  let sxLast = {...sxAll, marginRight: 0};
	return (
		<Box sx={{...sx, display: "flex"}}>
			{box.map((val, i) => (
				<TextField
					key={i}
					sx={i === 0 ? sxFirst : i === box.length - 1 ? sxLast : sxAll}
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label={prefix + axisNames[i]}
					value={box[i]}
					onChange={arraySetter(i)}
				/>
			))}
		</Box>
	)
}

const computationTypes = {
	0: "Approximate",
	1: "Exact",
	2: "Automatic",
};

const nestingTypes = {
	1048576: "Standard",
	1048580: "Balanced",
	133: "Exposure",
	512: "2D Standard",
	1536: "2D Balanced",
	360448: "Uniform",
	16384: "Volume minimization",
	260: "Count Balanced",
};

function NestingVolume({rootNesting, setRootNesting}){
	const [nesting, setNesting] = useState(rootNesting);
	const sx = {
		width: "100%",
		m: 2,
	}
	return (
		<Box component="form">
			<Container maxWidth='sm'>
				<TextField
					sx={sx}
					id="name"
					label="Name"
					variant="standard"
					defaultValue={nesting.name}
					onChange={(evt) => setNesting({...nesting, name: evt.target.value})}
				/>
			</Container>
			<Container maxWidth='sm'>
				<TextField
					sx={sx}
					value={nesting.exact_dimensions}
          select
					label="Exact dimensions"
					onChange={(evt) => setNesting({...nesting, exact_dimensions: evt.target.value})}
				>
					{Object.entries(computationTypes).map(([key, value]) => (
						<MenuItem key={key} value={key}>{value}</MenuItem>
					))}
				</TextField>
			</Container>
			<Container maxWidth='sm'>
				<TextField
					sx={sx}
          select
					value={nesting.method}
					label="Nesting method"
					onChange={(evt) => setNesting({...nesting, method: evt.target.value})}
				>
					{Object.entries(nestingTypes).map(([key, value]) => (
						<MenuItem key={key} value={key}>{value}</MenuItem>
					))}
				</TextField>
			</Container>
			<Container maxWidth='sm'>
        <BoxField box={nesting.tray} setBox={fieldSetter(nesting, setNesting, "tray")} prefix=""  sx={sx}/>
			</Container>
      <Container maxWidth='sm'>
				<TextField
          sx={sx}
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label="Number of trays"
					value={nesting.n_trays}
					onChange={(evt) => setNesting({...nesting, n_trays: evt.target.value})}
				/>
      </Container>
			<Container sx={{display: "flex", flexDirection: "row-reverse"}}>
				<Button onClick={() => setRootNesting(nesting)}>Submit</Button>
			</Container>
		</Box>
	)
}

function isItemValid(item){
  return item.display_model || (item.size && (item.size[0] * item.size[1] * item.size[2] !== 0));
}

function LoadIcon({item}){
	let sx;
  let tooltip;
  let icon;
	if(isItemValid(item)){
		sx = {color: "success.main"};
    tooltip = "Item was successfully loaded";
    icon = (<CheckCircleIcon fontSize="small" sx={sx}/>);
	}else{
		sx = {color: "error.main"};
    tooltip = "We were unable to import this model";
    icon = <CancelIcon fontSize="small" sx={sx}/>;
	}
	return (
    <Tooltip title={tooltip}>
      <span>
        {icon} 
      </span>
    </Tooltip>
	)
}

const InterlockingSelect = memo(function InterlokingSelect({val, onChange}){
  return (
    <Select
      sx={{minWidth: "125px", width: "100%"}}
      value={val}
      label="Interlocking avoidance"
      onChange={onChange}
    >
      <MenuItem value={1}>Traps</MenuItem>
      <MenuItem value={2}>Chains</MenuItem>
      <MenuItem value={4}>Everything</MenuItem>
      <MenuItem value={5}>Stacks</MenuItem>
    </Select>
  );
});

const NestedItem = memo(function NestedItem({item, setItem}){
	const setMargins = (item) => {setItem(item, "margins")};
  const setInterlocking = useCallback((evt) => {
    return setItem({...item, interlocking_check: evt.target.value}, "interlocking_check");
  }, [item, setItem]);
	return (
		<TableRow>
			<TableCell sx={{minWidth: "150px" }}>
				<Checkbox checked={item.checked} onChange={(evt) => setItem({...item, checked: evt.target.checked}, "checked")}/>
				{item.name}
			</TableCell>
			<TableCell>
				<TextField
					sx={{...numberInputSx, width: "100%"}}
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label="Quantity"
					value={item.quantity}
					onChange={(evt) => setItem({...item, quantity: evt.target.value}, "quantity")}
				/>
			</TableCell>
			<TableCell>
				<Select
					sx={{minWidth: "185px", width: "100%"}}
					value={item.degrees_of_freedom}
					label="Movement contraints"
					onChange={(evt) => setItem({...item, degrees_of_freedom: evt.target.value}, "degrees_of_freedom")}
				>
					<MenuItem value={0}>No constraints</MenuItem>
					<MenuItem value={1}>Fixed bottom plane</MenuItem>
					<MenuItem value={2}>No rotations</MenuItem>
					<MenuItem value={3} disabled={item.quantity > 1}>Unmovable</MenuItem>
					<MenuItem value={4} disabled={item.quantity > 1}>No build zone</MenuItem>
				</Select>
			</TableCell>
			<TableCell>
        <InterlockingSelect
          val={item.interlocking_check}
          onChange={setInterlocking}
        />
			</TableCell>
			<TableCell>
				<TextField
					sx={{...numberInputSx, width: "100%"}}
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label="Minimal distance"
					value={item.minimal_distance}
					onChange={(evt) => setItem({...item, minimal_distance: evt.target.value}, "minimal_distance")}
				/>
			</TableCell>
			<TableCell>
				<BoxField box={item.margins} setBox={fieldSetter(item, setMargins, "margins")}/>
			</TableCell>
		</TableRow>
	);
});

function getSinterBoxName(task){
  if(task.name){
    return task.name
  }else{
    return (new Date(task.created)).toLocaleString()
  }
}

function SelectSinterBoxes({addSinterBoxes, setModal}){
	const [tasks, setTasks] = useState([]);
	const [checkedTasks, setCheckedTasks] = useState({});
	useEffect(() => {
		fetchAllPages("/api/nesting_tasks/").then((data) => {
			setTasks(data);
		});
	}, []);

	function addSelectedSinterBoxes(){
		const selectedSinterBoxes = tasks.filter((task) => {
			return checkedTasks[task.slug];
		});
		addSinterBoxes(selectedSinterBoxes);
		setModal(false);
	}
	return (
		<>
			<List dense={true}>
				{tasks.map((task) => (
					<ListItem key={task.slug}>
						<ListItemButton>
							<Checkbox
								onChange={(evt) => {
									const newCheckedTasks = {...checkedTasks};
									newCheckedTasks[task.slug] = evt.target.value;
									setCheckedTasks(newCheckedTasks);
								}}
								value={checkedTasks[task.slug]}
							/>
						</ListItemButton>
						<ListItemText primary={task.group_name} secondary={getSinterBoxName(task)}/>
					</ListItem>
				))}
			</List>
			<Container sx={{display: "flex"}}>
				<Button component="label" onClick={() => setModal(false)}>
					Cancel
				</Button>
				<Box sx={{flex: 1}}/>
				<Button onClick={() => addSelectedSinterBoxes()}>Submit</Button>
			</Container>
		</>
	);
}

function newItem(itemSpecifics, nesting, quantity=1, dof=1, lock=1, dmin=5, margins=[0, 0, 0, 0, 0, 0]){
	return {
		...itemSpecifics,
		checked: false,
		quantity: quantity,
		degrees_of_freedom: dof,
		interlocking_check: lock,
		minimal_distance: dmin,
		margins: margins,
		group: nesting.slug,
	};
}

let counter = 1;

function LoadingBar({
  loading,
  progress,
}){
  if(loading){
    return (<LinearProgress variant="determinate" value={progress*100} />);
  }else{
    return (<></>);
  }
}

function ClosableModal({children, onClose, boxComponent="div", title="", ...params}){
  return (
    <Modal {...params} onClose={onClose}>
      <Box sx={style} component={boxComponent}>
        <Box sx={{display: "flex", alginItems: "center", justifyContent: "center"}}>
          {title}
          <Box sx={{flexGrow: 1}}/>
          <IconButton
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {children}
      </Box>
    </Modal>
  );
}

function NestedItems({
	nesting,
	rootItems,
	setRootItems,
	rootSinterBoxes,
	setRootSinterBoxes,
  onClose,
}){
	const [items, setItems] = useState(rootItems);
  const [uploadedDesigns, setUploadedDesigns] = useState([]);
	const [sinterBoxes, setSinterBoxes] = useState(rootSinterBoxes);
  const [progress, _setProgress] = useState(0);
	const [loading, _setLoading] = useState(false);
	const [modal, setModal] = useState(false);
  const [page, setPage] = useState(0);
  const [nRows, setNRows] = useLocalStorage("edit-content-n-rows", 5);
  const nwsFetch = useContext(FetchContext);
  function setProgress(cur, total){
    console.log(cur, total);
    _setProgress(cur / total);
  }
	function setLoading(lding){
		_setLoading(lding);
		if(!lding){
			_setProgress(0);
		}
	}
	function inc() {
		const val = counter;
		counter = val + 1;
		return val;
	}
	function newList(newItem, field, itemList){
		const val = newItem[field];
		return itemList.map(item => {
			if((newItem.slug && item.slug && item.slug === newItem.slug)
				|| (newItem.tmpId && item.tmpId && newItem.tmpId === item.tmpId))
			{
				return newItem;
			}else if(item.checked && field !== "checked"){
				let modifiedItem = {...item};
				modifiedItem[field] = val;
				return modifiedItem;
			}else{
				return item;
			}
		});
	}
	function setItem(newItem, field=""){
		return setItems((prevItems) => newList(newItem, field, prevItems));
	};
	function setSinterBox(newItem, field=""){
		return setSinterBoxes((prevSinterBoxes) => newList(newItem, field, prevSinterBoxes));
	};
	async function addItems(files){
		setLoading(true);
    let total = files.length;
    let cur = 0;
		const designs = await Promise.all(
			Array.from(files).map(async (file) => {
				let formData = new FormData()
				formData.append("model_file", file);
				formData.append("auto_remove", true);
				let response = await nwsFetch("/api/designs/", {
					method: "POST",
					body: formData,
				});
        cur += 1;
        setProgress(cur,total);
				return [file.name, await response.json(), response.ok];
			})
		);
    const validDesigns = designs.filter(
      ([name, response, isOk]) => isOk
    );
    setUploadedDesigns([...uploadedDesigns, ...validDesigns.map(
      ([name, design, isOk])=>{return design.slug}
    )]);
		setItems([
			...items,
			...validDesigns.map(([name, design, isOk]) => {
				return newItem({name: name, design: design.slug, tmpId: inc(), type: "nd"}, nesting);
			})
		]);
		setLoading(false);
	}
	async function addSinterBoxes(tasks){
		setLoading(true);
    let total = tasks.length;
    let cur = 0;
		setSinterBoxes([
			...sinterBoxes,
			...tasks.map((task) => {
        setProgress(cur,total);
        cur += 1;
				return newItem({name: getSinterBoxName(task), source_task: task.slug, tmpId: inc(), type: "si"}, nesting);
			})
		]);
		setLoading(false);
	}
	function getUnchecked(){
		function isUnchecked(item){
			return !(item.checked);
		}
		return [items.filter(isUnchecked), sinterBoxes.filter(isUnchecked)];
	}
	function totalUnchecked(){
		const [uncheckedItems, uncheckedSinterBoxes] = getUnchecked();
		return uncheckedItems.length + uncheckedSinterBoxes.length;
	}
	async function deleteItems(){
		setLoading(true);
		const [uncheckedItems, uncheckedSinterBoxes] = getUnchecked();
		await setItems(uncheckedItems);
		await setSinterBoxes(uncheckedSinterBoxes);
		setLoading(false);
	}
	function uploadFiles(evt){
		addItems(evt.target.files);
	}
	function updateNesting(){
		setRootItems(items);
		setRootSinterBoxes(sinterBoxes);
	}
	function openModal(){
		setModal(true);
	}
	function closeModal(){
		setModal(false);
	}
  function cancel(){
    uploadedDesigns.forEach((designSlug) => {
				nwsFetch("/api/designs/" + designSlug + "/", {
					method: "DELETE",
				});
    });
    onClose()
  }
	/* Upload button
	 * taken from https://stackoverflow.com/questions/40589302/how-to-enable-file-upload-on-reacts-material-ui-simple-input
	 * question by: https://stackoverflow.com/users/3667635/gintoki27
	 * edited by: https://stackoverflow.com/users/3865116/vedran
	 * answered by: https://stackoverflow.com/users/2736037/elijahcarrel
	 * */
  const fullList = items.concat(sinterBoxes);
  const itemsSlice = nRows > 0 ? fullList.slice((page)*nRows, (page + 1)*nRows) : fullList;
	return (
		<Box>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Quantity</TableCell>
							<TableCell>Movement constraints</TableCell>
							<TableCell>Interlocking avoidance</TableCell>
							<TableCell>Minimal distance</TableCell>
							<TableCell>Margins</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
            {itemsSlice.map((item, i) => {
              if(item.type === "nd"){
                return (<NestedItem key={i + "-" + item.slug} item={item} setItem={setItem}/>);
              }else{
                return (<NestedItem key={i + "-" + item.slug} item={item} setItem={setSinterBox}/>);
              }
            })}
					</TableBody>
          <TableFooter>
						<TableRow>
             <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              rowsPerPage={nRows}
              onRowsPerPageChange={(evt) => {
                setNRows(evt.target.value)
              }}
              colSpan={3}
              count={items.length}
              page={page}
              onPageChange={(evt, pageNumber) => {
                setPage(pageNumber)
              }}
            />
						</TableRow>
          </TableFooter>
				</Table>
			</TableContainer>
      <LoadingBar 
        loading={loading}
        progress={progress}
      />
			<Container sx={{display: "flex"}}>
				<Button component="label">
					Add parts
					<input
						type="file"
						hidden
						multiple
						onChange={uploadFiles}
					/>
				</Button>
				<Button component="label" onClick={openModal}>
					Add Sinter Boxes
				</Button>
				<Button disabled={totalUnchecked() === (items.length + sinterBoxes.length)} onClick={deleteItems}>
					Delete parts
				</Button>
				<Box sx={{flex: 1}}/>
        <Button
          onClick={cancel}
          disabled={loading}
        >
          Cancel
        </Button>
				<Button
					onClick={updateNesting}
					disabled={loading}
				>
					Submit
				</Button>
			</Container>
			<ClosableModal
				open={modal}
				onClose={closeModal}
        title={(<Typography variant="h6" component="h6">Previous results</Typography>)}
			>
				<SelectSinterBoxes addSinterBoxes={addSinterBoxes} setModal={setModal}/>
			</ClosableModal>
		</Box>
	);
}

async function fetchAllPages(url){
	let items = [];
	const request = await fetch(url);
	let page = await request.json()
	items = [...items, ...page.results];
	while(page.next){
		const request = await fetch(page.next);
		page = await request.json()
		items = [...items, ...page.results];
	}
	return items;
}

function TaskResultDownload({downloadNesting, progress}){
	if(progress === null || progress === 1.0){
		return (
      <Tooltip title="Download the results">
        <IconButton onClick={downloadNesting}><DownloadIcon/></IconButton>
      </Tooltip>
		);
	}else{
		return (
			<Icon>
				<CircularProgress
					style={{height:24, width:24}}
					variant="determinate"
					value={progress * 100}
				/>
			</Icon>
		);
	}
}

function NestingTaskControl({task, removeTask, stopTask, webSocket, taskName, setTaskName}){
	const navigate = useNavigate();
	const [progress, setProgress] = useState(null);
  const [modal, setModal] = useState(false);
  const nwsFetch = useContext(FetchContext);
	async function duplicateNesting(){
		const response = await nwsFetch(
			"/api/nesting_tasks/" + task.slug + "/copy/", {
				method: "POST",
			},
		);
		if(response.ok){
			const groupCopy = await response.json();
			navigate("/nesting/" + groupCopy.slug);
		}
	}
	async function downloadNesting(){
		const response = await nwsFetch(
			"/api/nesting_tasks/" + task.slug + "/download/", {
				method: "POST",
			},
		);
		// taken from stackoverflow:
		// https://stackoverflow.com/questions/32545632/how-can-i-download-a-file-using-window-fetch
		// question by: https://stackoverflow.com/users/1270566/zachguo
		// edited by: https://stackoverflow.com/users/11107541/starball
	// answer by: https://stackoverflow.com/users/1003831/lucas-matos
		// edited by: https://stackoverflow.com/users/3290397/golopot
		async function handleArchive(response){
			const blob = await response.blob();
			const file = window.URL.createObjectURL(blob);
			window.location.assign(file);
		}
		if(response.ok && response.status === 200){
			await handleArchive(response);
		}else if(response.ok && response.status === 201){
			async function downloadProgress(evt){
				const message = JSON.parse(evt.data);
				if(message.archive && message.task === task.slug){
					const archiveResponse = await fetch(message.archive);
					await handleArchive(archiveResponse);
					webSocket.removeEventListener("message", downloadProgress);
				}else if(message.export_progress && message.task === task.slug){
					setProgress(message.export_progress);
				}
			}
			webSocket.addEventListener("message", downloadProgress);
		}
	}
  async function changeTaskName(){
    const response = await nwsFetch(
      "/api/nesting_tasks/" + task.slug + "/",
      {
        method: "PATCH",
        body: JSON.stringify({
          name: taskName,
        }),
      },
    );
    if(response.ok){
      setModal(false);
    }
  }
	if(task.alive && task.nesting_task && task.nesting_task.state === "PENDING"){
		return (
			<IconButton onClick={stopTask}><StopCircleIcon/></IconButton>
		);
	}else{
		return (
			<>
        <Tooltip title="Change the name of the task">
          <IconButton onClick={()=>{setModal(true)}}><EditIcon/></IconButton>
        </Tooltip>
        <Tooltip title="Copy the results to a new nesting">
          <IconButton onClick={duplicateNesting}><ContentCopyIcon/></IconButton>
        </Tooltip>
        <Tooltip title="Delete the results">
          <IconButton onClick={removeTask}><DeleteIcon/></IconButton>
        </Tooltip>
      <TaskResultDownload downloadNesting={downloadNesting} progress={progress} />
        <ClosableModal
          open={modal}
          onClose={()=>{setModal(false)}}
          title={(<Typography variant="h6" component="h6">Rename</Typography>)}
        >
          <TextField
            label="Change task name"
            value={taskName}
            onChange={(evt) => setTaskName( evt.target.value)}
          />
          <Container sx={{display: "flex", flexDirection: "row-reverse"}}>
            <Button onClick={() => changeTaskName()}>Submit</Button>
          </Container>
        </ClosableModal>
			</>
		);
	}
}

function TaskTrayDetails({
  properties,
  unit=""
})
{
  return (
    <>
    {properties.map((val, i) => (
      <Fragment key={"prop" + i}>
        <Typography
          component="span"
          variant="body2"
          sx={{ color: 'text.primary', display: 'inline' }}
        >
          {"Tray#" + (i + 1) + ": "}
        </Typography>
        {val.toFixed(2)} {unit}
        <br/>
      </Fragment>
    ))}
    </>
  )
}

function TaskTrayExposure({
  curves,
  properties,
  unit=""
}){
  return (
    <>
    {curves.map((curve, i) => (
      <Fragment key={"prop" + i }>
        <Typography
          component="span"
          variant="body2"
          sx={{ color: 'text.primary', display: 'inline' }}
        >
          {"Tray#" + (i + 1) + ": "}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          sx={{ color: 'text.secondary', display: 'inline' }}
        >
          {properties[i].toFixed(2)} {unit}
        </Typography>
        <LineChart
          xAxis={[{data: [...Array(curve["curve"].length).keys()].map((val) => val*curve["step"])}]}
          series={[
            {
              data: curve["curve"].map((val) => val > 0 ? val: 0),
              showMark: false,
            }
          ]}/>
      </Fragment>
    ))}
    </>
  )
}

function NestingTaskDetails({
  task,
  result,
  highlighted,
})
{
  if(task && result && highlighted){
    let exposure = "N/A";
    const slots = {}
    if(task && result && result.curves && result.curves.length > 0){
      exposure = (<TaskTrayExposure curves={result.curves} properties={result.exposure_curve_std_deviations}/>);
      slots.secondary = Box;
    }
    let densities = [];
    if(task && result && result.densities){
      densities = result.densities.map((density) => density * 100);
    }
    let heights = [];
    if(task && result && result.heights){
      heights = result.heights;
    }
    let nout = 0;
    if(task && result && result.nout){
      nout = result.nout;
    }
    return (
      <Collapse in={highlighted}>
        <List component="div" disablePadding dense={true}>
          <ListItemText sx={{ pl: 4 }}
            primary="Height"
            secondary=<TaskTrayDetails properties={heights}/>
          />
          <ListItemText sx={{ pl: 4 }}
            primary="Density"
            secondary=<TaskTrayDetails properties={densities} unit="%"/>
          />
          <ListItemText sx={{ pl: 4 }} slots={slots}
            primary="Exposure"
            secondary={exposure}
          />
          <ListItemText sx={{ pl: 4 }}
            primary="Part Count"
            secondary={"In: "+result.results.length+" / Out: "+nout}
          />
        </List>
      </Collapse>
    );
  }else{
    return (<></>);
  }
}

function NestingTask({task, removeTask, stopTask, highlighted, setCurrentRun, webSocket}){
  const [pProgress, setPProgress] = useState({preparing: false, progress: 0});
  const [modal, setModal] = useState(false);
	const date = new Date(task.created);
  const [taskName, setTaskName] = useState(task.name ? task.name : date.toLocaleString());
  const results = useContext(TaskContext);
  const result = results[task.slug];
	function _removeTask(evt){
		removeTask(evt);
		if(highlighted){
			setCurrentRun(null);
		}
    setModal(false);
	}
  async function preparationProgress(evt){
    const message = JSON.parse(evt.data);
    if(message.preparation && message.task === task.slug){
      const progress = message.preparation;
      if(progress !== 1){
        setPProgress({preparing: true, progress: progress});
      }else{
        setPProgress({preparing: false, progress: 0});
        webSocket.removeEventListener("message", preparationProgress);
      }
    }
  }
  let sx = {};
  let title = "";
  if(!task.relevant){
    sx = {background: red[100]};
    title = "Modifications to the nesting configuration and content were made after running this task";
  }
  if(highlighted){
    sx["textDecoration"] = "underline";
  }
  webSocket.addEventListener("message", preparationProgress);
	return (
    <>
      <ListItem key={task.slug}>
        <ListItemButton sx={sx}>
          <Tooltip title={title}>
            <ListItemText
              primaryTypographyProps={{noWrap: true}}
              primary={taskName}
              onClick={() => {setCurrentRun(task)}}
            />
          </Tooltip>
        </ListItemButton>
        <NestingTaskControl
          task={task}
          taskName={taskName}
          setTaskName={setTaskName}
          removeTask={() => setModal(true)}
          stopTask={stopTask}
          webSocket={webSocket}
        />
      </ListItem>
      <LoadingBar
        loading={pProgress.preparing}
        progress={pProgress.progress}
      />
      <NestingTaskDetails task={task} result={result} highlighted={highlighted} />
      <Modal
        open={modal}
        onClose={()=>{setModal(false)}}
      >
        <Box sx={style}>
          <Typography>Are you sure you want to delete nesting task: {taskName}</Typography>
          <Container sx={{display: "flex"}}>
              <Button onClick={() => setModal(false)}>No</Button>
              <Box sx={{flexGrow: 1}}></Box>
              <Button onClick={(evt) => _removeTask(evt)}>Yes</Button>
          </Container>
        </Box>
      </Modal>
    </>
	)
}

function round2Decimals(v){
  return Math.round(v*100)/100;
}

function ItemTransformationDetails({item, setItem, save, close, nesting}){
  const [szLocked, setSzLocked] = useState(true);
	const pos = ["X", "Y", "Z"];
	const rotation = [["X", "x"], ["Y", "y"], ["Z", "z"]];
	const x = item.eulerAngles.x * 180 / Math.PI;
	const y = item.eulerAngles.y * 180 / Math.PI;
	const z = item.eulerAngles.z * 180 / Math.PI;
	const [tmpEuler, setTmpEuler] = useState([x, y, z]);
	useEffect(() => {
		setTmpEuler([x, y, z]);
	}, [x, y, z]);
  let tray_id = item.tray_id;
  let trays = [...Array(nesting.n_trays).keys()];
  let trayStyle = {display: "block"};
  let trayTooltip = "";
  let error = "";
  if(item.tray_id >= nesting.n_trays){
    trayStyle["background"] = red[100];
    trayTooltip = "The assigned tray was removed";
    error = (<FormHelperText>{trayTooltip}</FormHelperText>);
  }
  let icon;
  if(szLocked){
    icon = (<LockIcon/>);
  }else{
    icon = (<LockOpenIcon/>);
  }
  const scale = getScale(item);
	return (
		<>
			<Typography align="left">
				Tray
			</Typography>
        <Select
          label="Tray"
          value={tray_id}
					sx={trayStyle}
					variant="filled"
					size="small"
          title={trayTooltip}
					onChange={(evt) => {
						setItem({...item, tray_id: evt.target.value});
					}}
        >
              <MenuItem key={-1} value={-1}>Any</MenuItem>
          {trays.map((tray, i) => (
              <MenuItem key={"tray"+tray} value={tray}>Tray #{tray+1}</MenuItem>
          ))}
				</Select>
        {error}
			<Typography align="left">
				Location
			</Typography>
			{pos.map((ax, idx) => 
				<TextField
					key={"pos-" + ax}
					sx={{display: "block"}}
					variant="filled"
					size="small"
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label={ax}
					value={item.position[idx]}
					disabled={item.quantity !== 1 || item.degrees_of_freedom < 3} 
					onChange={(evt) => {
						const position = [...item.position];
						position[idx] = evt.target.value;
						setItem({...item, position: position});
					}}
				/>
			)}
			<Typography align="left">
				Rotation (Euler XYZ)
			</Typography>
			{rotation.map(([ax, idx], i) => 
				<TextField
					key={"rot-" + ax}
					sx={{display: "block"}}
					variant="filled"
					size="small"
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label={ax}
					value={tmpEuler[i]}
					onChange={(evt) => {
						const _tmpEuler = [...tmpEuler];
						_tmpEuler[i] = evt.target.value;
						setTmpEuler(_tmpEuler);
						const euler = item.eulerAngles.clone();
						euler[idx] = evt.target.value * Math.PI / 180;
						if(!isNaN(euler[idx])){
							setItem({...item, eulerAngles: euler});
						}
					}}
				/>
			)}
			<Typography align="left">
				Size <IconButton onClick={()=>setSzLocked(!szLocked)}>{icon}</IconButton>
			</Typography>
			{pos.map((ax, idx) => 
				<TextField
					key={"sca-" + ax}
					sx={{display: "block"}}
					variant="filled"
					size="small"
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label={ax}
					value={round2Decimals(item.size[idx])}
					onChange={(evt) => {
						const size = [...item.size];
						size[idx] = Number(evt.target.value);
            if(szLocked){
              const scale = size[idx]/item.mesh_size[idx];
              size[(idx + 1)%3] = scale * item.mesh_size[(idx+1)%3];
              size[(idx + 2)%3] = scale * item.mesh_size[(idx+2)%3];
            }
						setItem({...item, size: size});
					}}
				/>
			)}
			<Typography align="left">
				Scale
			</Typography>
			{pos.map((ax, idx) => 
				<TextField
					key={"sca-" + ax}
					sx={{display: "block"}}
					variant="filled"
					size="small"
					inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
					label={ax}
					value={round2Decimals(scale[idx], 2)}
					onChange={(evt) => {
            const scale = Number(evt.target.value);
						const size = [...item.size];
						size[idx] = scale * item.mesh_size[idx];
						setItem({...item, size: size});
					}}
				/>
			)}
		</>
	)
}

function getScale(item){
  return [
    item.size[0] / item.mesh_size[0],
    item.size[1] / item.mesh_size[1],
    item.size[2] / item.mesh_size[2],
  ]
}

function CurrentItemTransformationDetails({currentItem, items, save, close, nesting}){
	const [item, setItem] = useState(null);
	useEffect(() => {
		const _item = items[currentItem];
		const quaternion = new Quaternion(
			_item.rotation[1],
			_item.rotation[2],
			_item.rotation[3],
			_item.rotation[0],
		);
		const eulerAngles = new Euler();
		eulerAngles.setFromQuaternion(quaternion);
		setItem({..._item, eulerAngles: eulerAngles.clone()});
	}, [currentItem, items, setItem]);
	const [mode, setMode] = useState(null);
	const [selectBottomFaceActive, setSelectBottomFaceActive] = useState(false);
  function selectTransformMode(newMode){
    if(selectBottomFaceActive){
      viewerState.deactivateSelectBottomFace();
      setSelectBottomFaceActive(false);
    }
    if(mode === newMode){
      setMode(null);
      viewerState.hidePartControl();
    }else{
      setMode(newMode);
      viewerState.setControlMode(newMode);
      viewerState.showPartControl();
    }
  }
  function toggleSelectBottomFace(){
    if(selectBottomFaceActive){
      viewerState.deactivateSelectBottomFace();
      setSelectBottomFaceActive(false);
    }else{
      if(mode !== null){
        setMode(null);
        viewerState.hidePartControl();
      }
      viewerState.activateSelectBottomFace();
      setSelectBottomFaceActive(true);
    }
  }
  useEffect(() => {
    viewerState.hidePartControl();
    return () => {
      viewerState.deactivateSelectBottomFace();
      viewerState.hidePartControl();
    };
  }, []);
	useEffect(() => {
		if(item){
			viewerState.setTransform(
				item.slug,
				item.position,
				item.eulerAngles,
				getScale(item),
        item.tray_id,
			);
			viewerState.setListener((position, euler, scale) => {
				const newItem = {...item,
					position: position.toArray(),
					eulerAngles: euler.clone(),
					size: [
            item.mesh_size[0] * scale.x,
            item.mesh_size[1] * scale.y,
            item.mesh_size[2] * scale.z,
          ],
				}
				setItem(newItem);
			});
			return () => {
				viewerState.clearListener()
			};
		}
	}, [item]);
	if(item){
		return (
			<Breakout>
				<ItemTransformationDetails
          item={item}
          setItem={setItem}
          save={save}
          close={close}
          nesting={nesting}/>
				<Box sx={{display: "flex"}}>
					<IconButton sx={{flexGrow: 1}} onClick={()=>selectTransformMode("translate")} color={mode === "translate" ? "primary" : "default"}>
						<TrendingFlatIcon/>
					</IconButton>
					<IconButton sx={{flexGrow: 1}} onClick={()=>selectTransformMode("rotate")} color={mode === "rotate" ? "primary" : "default"}>
						<PivotTableChartIcon/>
					</IconButton>
					<IconButton sx={{flexGrow: 1}} onClick={()=>selectTransformMode("scale")} color={mode === "scale" ? "primary" : "default"}>
						<AspectRatioIcon/>
					</IconButton>
				</Box>
				<Tooltip title="Click on a face to set it as the bottom">
					<IconButton
						sx={{width: "100%"}}
						onClick={toggleSelectBottomFace}
						color={selectBottomFaceActive ? "primary" : "default"}
					>
						<VerticalAlignBottomIcon/>
						<Typography variant="body2" sx={{ml: 1}}>Select Bottom Face</Typography>
					</IconButton>
				</Tooltip>
				<Box sx={{display: "flex"}}>
					<Button sx={{flexGrow: 1}} onClick={() => {
						const quaternion = new Quaternion();
						quaternion.setFromEuler(item.eulerAngles);
						save(
							item.position,
							[quaternion.w, quaternion.x, quaternion.y, quaternion.z],
							item.size,
              item.tray_id,
						);
					}}>Save</Button>
					<Button sx={{flexGrow: 1}} onClick={close}>Close</Button>
				</Box>
			</Breakout>
		)
	}else{
		return (
			<></>
		)
	}
}

function NestedComponentListItem({
	type, item, idx, currentItem, setCurrentItem, nTrays
}){
  let style = {};
  let tooltip = "";
  if(currentItem.idx === idx && type === currentItem.type){
    style = {background: blue[100]}
  }else if(item.tray_id >= nTrays){

    style = {background: red[100]}
    tooltip = "This item is assigned to tray that was removed";
  }
  let onClick = () => {};
  let disabled = true;
  if(isItemValid(item)){
    onClick = () => {
      if(idx !== currentItem.idx){
        setCurrentItem(type, idx)
      }else{
        setCurrentItem(null, null);
      }
    }
    disabled = false;
  }
	return (
    <ListItem divider disablePadding>
      <ListItemButton
        style={style}
        key={"list-item-text" + item.slug}
        onClick={onClick}
        disabled={disabled} 
      >
        <Tooltip title={tooltip}>
          <span>
            {item.quantity}&times;{item.name}
          </span>
        </Tooltip>
      </ListItemButton>
      <ListItemIcon><LoadIcon item={item}/></ListItemIcon>
    </ListItem>
	)
}

function tasksReducer(tasksResults, message){
  if(message){
    const newTasksResults = {...tasksResults};
    newTasksResults[message.task] = {...message};
    return newTasksResults;
  }else{
    return tasksResults;
  }
}

function NestingTaskList({
	children, currentItem, currentRun, items, sinterBoxes, webSocket, nestingRuns
}){
  const [tasksResults, dispatchTask] = useReducer(tasksReducer, {}); 

  useEffect(() => {
    nestingRuns.forEach((run) => {
      if(run.result){
        if(tasksResults.hasOwnProperty(run.slug)){
          const currentDate = new Date(tasksResults[run.slug].created);
          const newDate = new Date(run.result.created);
          if(newDate > currentDate){
            dispatchTask(run.result);
          }
        }else{
          dispatchTask(run.result);
        }
      }
    });
  }, [tasksResults, dispatchTask, nestingRuns]);

  useEffect(() => {
    if(currentItem.idx !== null){
      const item = currentItem.type === "nd" ? items[currentItem.idx] : sinterBoxes[currentItem.idx];
      console.log("displaying: " + item.slug);
      viewerState.displayItem(item.slug, item.tray_id);
    }else if(currentRun !== null){
      const result = tasksResults[currentRun];
      if(result){
        console.log("displaying: " + result.task);
        viewerState.createOrUpdateMeshInstances(result.results);
      }
    }
    return () => {
      viewerState.clearScene()
    }
  }, [currentItem, currentRun, items, sinterBoxes, tasksResults]);
  
	useEffect(() => {
    function updateResult(evt){
      let message;
      try{
        message = JSON.parse(evt.data);
      }catch(e){
        console.error(e);
        throw e;
      }
			if(message.results){
        dispatchTask(message);
			}
    }
    if(webSocket){
      webSocket.addEventListener("message", updateResult);
    }
		return () => {
      if(webSocket){
        webSocket.removeEventListener("message", updateResult);
      }
    }
  }, [dispatchTask, webSocket]);

  return (
    <TaskContext value={tasksResults}>
        {children}
    </TaskContext>
  );
}

function NestingConfiguration({
	nesting, setNesting,
	items, setItems, setSingleItem,
	nestingRuns, setNestingRuns,
	currentRun, _setCurrentRun,
	webSocket,
	sinterBoxes, setSinterBoxes,
  setSingleSinterBoxes,
}){
	const [modal, setModal] = useState(0);
  const nwsFetch = useContext(FetchContext);
	const [currentItem, _setCurrentItem] = useState({type: null, idx: null});
  const [playButtonAvailable, setPlayButtonAvailable] = useState(true);
	function setCurrentItem(type, idx){
		_setCurrentItem({type: type, idx: idx});
		if(idx !== null && type !== null){
			_setCurrentRun(null);
		}
	}
	function setCurrentRun(run){
		_setCurrentItem({type: null, idx: null});
    if(currentRun && run && run.slug === currentRun){
      _setCurrentRun(null);
    }else if(run){
      _setCurrentRun(run.slug);
    }else{
      _setCurrentRun(run);
    }
	}
	const pendingModels = items.filter((item) => {
		return item.display_model === null;
	});
  const invalidTrays = items.filter((item) => {
    return item.tray_id >= nesting.n_trays;
  });
	const ready = pendingModels.length === 0 && invalidTrays.length === 0;
	let tooltip;
	if(ready){
		tooltip = "Start a nesting job";
  }else if(invalidTrays){
		tooltip = "Some parts are assigned to a tray that was removed";
	}else{
		tooltip = "Some models are loaded yet or could not be processed";
	}
	async function nest() {
    setPlayButtonAvailable(false);
		const request = await nwsFetch(
			"/api/nesting_tasks/", {
				method: "POST",
				body: JSON.stringify({
					group: nesting.slug,
				}),
			},
		);
		if(request.ok){
			const newRun = await request.json()
      const currentlyRunning = nestingRuns.filter((run) => {
        return run.slug === currentRun && run.running;
      });
			setNestingRuns([...nestingRuns, newRun]);
      if(currentlyRunning.length === 0){
        setCurrentRun(newRun);
      }
      setPlayButtonAvailable(true);
		}
	}
	function taskRemover(task){
		async function removeTask(evt){
			evt.stopPropagation();
			const request = await nwsFetch(
				"/api/nesting_tasks/" + task.slug + "/", {
					method: "DELETE",
				}
			);
			if(request.ok){
				setNestingRuns(nestingRuns.filter((_task) => {
					return _task.slug !== task.slug;
				}));
			}
		}
		return removeTask;
	}
  function updateTask(updatedTask){
      setNestingRuns(nestingRuns.map((_task) => {
        if(_task.slug === updatedTask.slug){
          return updatedTask;
        }else{
          return _task;
        }
      }));
  }
	function taskStopper(task){
    // passive wait, the nesting job finishes on its own
    async function recieveStatus(evt){
      const message = JSON.parse(evt.data);
      if(message.task_id === task.nesting_task.id && message.hasOwnProperty("success")){
        updateTask({...task, nesting_task: {...task.nesting_task, state: "DONE"}});
        webSocket.removeEventListener("message", recieveStatus);
      }
    }
    webSocket.addEventListener("message", recieveStatus);

    // active wait, we asked for the nesting to stop
    async function listenTaskUpdates(evt){
      const message = JSON.parse(evt.data);
      if(message.task_id === task.nesting_task.id){
        const taskSlug = message.result;
        const response = await nwsFetch(
          "/api/nesting_tasks/" + taskSlug + "/",
          {method: "GET"},
        );
        if(response.ok){
          const updatedTask = await response.json();
          updateTask(updatedTask);
        }
        webSocket.removeEventListener("message", listenTaskUpdates);
      }
    }
		async function stopTask(evt){
			evt.stopPropagation();
			const request = await nwsFetch(
				"/api/nesting_tasks/" + task.slug + "/abort/", {
					method: "PATCH",
				}
			);
			if(request.status === 200){
				const updatedTask = await request.json();
        updateTask(updatedTask);
        // the task was aborted immediately, we do not need to wait for updates
        webSocket.removeEventListener("message", listenTaskUpdates);
			}else{
        webSocket.addEventListener("message", listenTaskUpdates);
      }
      // at this point listenTaskUpdates takes on
      webSocket.removeEventListener("message", recieveStatus);
		}
		return stopTask;
	}
  const hasCurrentItem = currentItem.idx !== null && 0 <= currentItem.idx && currentItem.idx < items.length;
  const invalidItems = items.filter((item) => {
    return item.tray_id >= nesting.n_trays;
  });
  let transformationDetails = (<></>);
  let editConfTooltip = "Change parts, their quantities and degrees of freedom";
  if(hasCurrentItem){
      editConfTooltip = "Save the configuration or close the edition menu for the current item"
			transformationDetails = (
        <CurrentItemTransformationDetails
          currentItem={currentItem.idx}
          items={currentItem.type === "si" ? sinterBoxes: items}
          close={() => setCurrentItem(null, null)}
          save={async (t, r, s, tid) => {
            const newItem = currentItem.type === "si" ? {...sinterBoxes[currentItem.idx]}: {...items[currentItem.idx]};
            newItem.position = t;
            newItem.rotation = r;
            newItem.size = s;
            newItem.tray_id = tid;
            if(currentItem.type === "nd" && await setSingleItem(newItem)){
              setCurrentItem(null, null);
            }else if(currentItem.type === "si" && await setSingleSinterBoxes(newItem)){
              setCurrentItem(null, null);
            }
          }}
          nesting={nesting}
        />
      )
  }else if (invalidItems.length > 0){
      editConfTooltip = "Some items are assigned to removed trays, fix them"
  }
  const border = '1px solid rgba(0, 0, 0, .125)';
  const detailsSx = {borderTop: border};
  const headerSx = {backgroundColor: 'rgba(0, 0, 0, .03)',};
  const accSx = {backgroundColor: 'rgba(0, 0, 0, .0)'};
  const runSx = {
    backgroundColor: "primary.main",
    paddingLeft: 2,
    boxShadow: 1
  };
  const [needConf, toggleNeedConf] = usePersistedToggle(nesting.slug + ".configured");
  const [needContent, toggleNeedContent] = usePersistedToggle(nesting.slug + ".content");
	return (
		<Container sx={{flexDirection: "column", display: "flex", paddingTop: 3, height:"100%"}}> 
      {transformationDetails}
      <Accordion
        expanded={needConf}
        square
        disableGutters
        sx={accSx}
        onChange={()=>toggleNeedConf()}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="panel1-content"
          id="panel1-header" sx={headerSx}
        >
          <Typography align="left" variant="h6" component="h6">
            Nesting configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <Typography align="left">Nesting: {nesting.name}</Typography>
          <Typography align="left">Size: {nesting.tray[3] - nesting.tray[0]}&times;{nesting.tray[4] - nesting.tray[1]}&times;{nesting.tray[5] - nesting.tray[2]}</Typography>
          <Typography align="left">Computations: {computationTypes[nesting.exact_dimensions]}</Typography>
          <Typography align="left">Method: {nestingTypes[nesting.method]}</Typography>
          <Divider variant="middle" sx={{margin: 0, paddingTop:2}}/>
          <Tooltip title={"Change nesting algorithm, tray size and quantities"}>
            <span>
              <Button onClick={() => setModal(1)}>Edit nesting</Button>
            </span>
          </Tooltip>
          <ClosableModal
            open={modal === 1}
            onClose={() => setModal(0)}
            title={(
              <Typography variant="h4" component="h4">
                Configure nesting
              </Typography>
            )}
          >
            <NestingVolume rootNesting={nesting} setRootNesting={(nesting) => {
              setModal(0);
              setNesting(nesting);
              toggleNeedConf();
            }} />
          </ClosableModal>
        </AccordionDetails>
      </Accordion>
      <Accordion
        square
        disableGutters
        sx={accSx}
        expanded={needContent}
        onChange={()=>toggleNeedContent()}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
           sx={headerSx}
        >
          <Typography align="left" variant="h6" component="h6">
            Nesting content
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <List sx={{ overflow: "auto", maxHeight: "30vh" }}>
            {items.map((item, idx) => (
              <NestedComponentListItem
                key={"nd-list-item" + item.slug}
                type="nd"
                item={item}
                idx={idx}
                currentItem={currentItem}
                setCurrentItem={setCurrentItem}
                nTrays={nesting.n_trays}
              />
            ))}
            {sinterBoxes.map((item, idx) => (
              <NestedComponentListItem
                key={"si-list-item" + item.slug}
                type="si"
                item={item}
                idx={idx}
                currentItem={currentItem}
                setCurrentItem={setCurrentItem}
                nTrays={nesting.n_trays}
              />
            ))}
          </List>
          <Tooltip title={editConfTooltip}>
            <span>
              <Button disabled={hasCurrentItem||(invalidItems.length > 0)} onClick={() => setModal(2)}>Edit content</Button>
            </span>
          </Tooltip>
          <ClosableModal
            open={modal === 2}
            title={(<Typography variant="h6" component="h6">Nesting Content</Typography>)}
            onClose={() => setModal(0)}
          >
            <NestedItems
              onClose={() => setModal(0)}
              nesting={nesting}
              rootItems={items}
              setRootItems={(items) => {
                setModal(0);
                setItems(items);
              }}
              rootSinterBoxes={sinterBoxes}
              setRootSinterBoxes={(sinterBoxes) => {
                setModal(0);
                setSinterBoxes(sinterBoxes);
              }}
            />
          </ClosableModal>
        </AccordionDetails>
      </Accordion>
      <Box sx={{flexGrow: 1, height:"100%"}}>
        <Typography align="left" variant="h6" component="h6" sx={runSx}>
          Nesting run
          <Tooltip title={tooltip}>
            <span>
              <IconButton sx={{color: "secondary.main"}} onClick={nest} disabled={!ready || !playButtonAvailable}><PlayCircleIcon fontSize={"large"}/></IconButton>
            </span>
          </Tooltip>
        </Typography>
        <List sx={{ overflow: "auto", maxHeight: "100%", border: border}} dense={true}>
          <NestingTaskList
            currentItem={currentItem}
            currentRun={currentRun}
            items={items}
            sinterBoxes={sinterBoxes}
            webSocket={webSocket}
            nestingRuns={nestingRuns}
          >
            {nestingRuns.map((task) => (
              <NestingTask
                key={task.slug}
                task={task}
                removeTask={taskRemover(task)}
                stopTask={taskStopper(task)}
                highlighted={currentRun === task.slug}
                setCurrentRun={setCurrentRun}
                webSocket={webSocket}
              />
            ))}
          </NestingTaskList>
        </List>
      </Box>
		</Container>
	)
}

async function getItems(slug){
	return [
		await fetchAllPages("/api/nested_designs/?group=" + slug + "&reference_frame=nesting"),
		await fetchAllPages("/api/nested_sinter_boxes/?group=" + slug + "&reference_frame=nesting"),
	];
}

function Nesting({nesting, setNesting}){
	const user = useContext(UserContext)[0];
  const nwsFetch = useContext(FetchContext);
	const [nestingRuns, setNestingRuns] = useState([]);
	useEffect(() => {
		viewerState.updateSceneAndMachineGeometry(
			nesting.tray[0],
			nesting.tray[1],
			nesting.tray[2],
			nesting.tray[3],
			nesting.tray[4],
			nesting.tray[5],
      nesting.n_trays,
		);
	}, [nesting.tray, nesting.n_trays]);
	const itemsRef = useRef();
	const currentRunRef = useRef();
	const nestingRunsRef = useRef();
	const sinterBoxesRef = useRef();
	const [items, _setItems] = useState([]);
	const [sinterBoxes, _setSinterBoxes] = useState([]);
	const [currentRun, setCurrentRun] = useState(null);
	const [webSocket, setWebSocket] = useState(null);
	const itemSetter = useCallback((items, baseSetter) => {
		let viewerItems = [];
		items.forEach(item => {
			if(item.display_model || item.size){
        item.scale = getScale(item);
				viewerItems.push(item);
			}
		});
		const pr = viewerState.addGeometries(viewerItems);
		baseSetter(items);
    return pr;
	}, []);
  const setItems = useCallback((items) => {
		return itemSetter(items, _setItems);
	}, [itemSetter, _setItems]);
	const setSinterBoxes = useCallback((items) => {
		return itemSetter(items, _setSinterBoxes);
	}, [itemSetter, _setSinterBoxes]);
	itemsRef.current = items
	currentRunRef.current = currentRun;
	nestingRunsRef.current = nestingRuns;
	sinterBoxesRef.current = sinterBoxes;
	useEffect(() => {
		function updateModels(evt){
      let message;
      try{
        message = JSON.parse(evt.data);
      }catch(e){
        console.error(e);
        throw e;
      }
			if(message.result){
				setItems(itemsRef.current.map(item => {
					if(item.design === message.result.slug){
						let modifiedItem = {...item};
						modifiedItem.display_model = message.result.display_model;
						return modifiedItem;
					}else{
						return item;
					}
				}));
			}
			if(message.sinter_box && message.size){
				setSinterBoxes(sinterBoxesRef.current.map(sinterBox => {
					if(sinterBox.slug === message.sinter_box){
						let modifiedSinterBox = {...sinterBox};
						modifiedSinterBox.size = message.size;
						return modifiedSinterBox;
					}else{
						return sinterBox;
					}
				}));
			}
		}
    let wsDomain = "";
    if(process.env.REACT_APP_WS_SERVER){
      wsDomain = process.env.REACT_APP_WS_SERVER;
    }
		const _webSocket = new WebSocket(
			wsDomain + "/ws/messaging/" + user.channel_slug + "/"
		)
		_webSocket.addEventListener("message", updateModels);
		setWebSocket(_webSocket);
		return () => {
			_webSocket.removeEventListener("message", updateModels);
			_webSocket.close();
		}
	}, [user.channel_slug, setItems, setSinterBoxes]);
	useEffect(() => {
    const taskFetch = new Promise((resolve, reject) => {
      fetchAllPages("/api/nesting_tasks/?group=" + nesting.slug).then((tasks) => {
        setNestingRuns(tasks);
        resolve(tasks);
      });
    });
    const itemFetch = new Promise((resolve, reject) => {
      getItems(nesting.slug).then(([items, sinterBoxes]) => {
        const itmPr = setItems(items);
        const sibPr = setSinterBoxes(sinterBoxes);
        Promise.all([itmPr, sibPr]).then(()=>{
          resolve([...items, ...sinterBoxes]);
        })
      });
    });
    Promise.all([taskFetch, itemFetch]).then(([tasks, items])=>{
      if(tasks.length > 0){
        setCurrentRun(tasks[tasks.length - 1].slug);
      }
    });
		return () => {
			viewerState.removeAllGeometries();
		}
	}, [nesting.slug, setItems, setSinterBoxes, setCurrentRun]);
	if(nesting.slug){
		async function setNestingAPI(nesting){
			const request = await nwsFetch(
				"/api/groups/", {
					method: "POST",
					body: JSON.stringify(nesting),
				}
			);
			if(request.ok){
				setNesting(await request.json());
			}
		}
		async function deleteRemoved(
			endpoint,
			newItems,
			baseItems
		){
			let itemSlugs = {};
			newItems.forEach((item) => {
				itemSlugs[item.slug] = true;
			});
			let deletedItems = [];
			baseItems.forEach((item) => {
				if(!(itemSlugs[item.slug])){
					deletedItems.push(item.slug);
				}
			});
			const request = await nwsFetch(
				endpoint + "destroy_multiple/", {
					method: "DELETE",
					body: JSON.stringify(deletedItems),
				}
			);
			if(request.ok){
				viewerState.removeGeometries(deletedItems);
			}
			return request.ok;
		}
		async function deleteRemovedItems(newItems){
			return await deleteRemoved(
				"/api/nested_designs/",
				newItems,
				items,
			);
		}
		async function setAPI(endpoint, newItems, deleteAPI, itemSetter){
			const request = await nwsFetch(
				endpoint, {
					method: "POST",
					body: JSON.stringify(newItems),
				}
			);
			if(request.ok){
				if(await deleteAPI(newItems)){
					itemSetter(await request.json());
				}
			}
			return request.ok;
		}
		async function setItemsAPI(newItems){
			return await setAPI(
				"/api/nested_designs/?reference_frame=nesting",
				newItems,
				deleteRemovedItems,
				setItems
			);
		}
    async function setSingleAPI(endpoint, modifiedItem, baseItems, itemSetter){
			const response = await nwsFetch(
				endpoint + modifiedItem.slug + "/?reference_frame=nesting", {
					method: "PUT",
					body: JSON.stringify(modifiedItem),
				}
			);
      if(response.ok){
        const respItem = await response.json();
        itemSetter(baseItems.map((item) => {
          if(item.slug === respItem.slug){
            return respItem;
          }else{
            return item;
          }
        }));
      }
    }
    async function setItemAPI(modifiedItem){
      return setSingleAPI("/api/nested_designs/", modifiedItem, items, setItems);
    }
		async function deleteRemovedSinterBoxes(newSinterBoxes){
			return await deleteRemoved(
				"/api/nested_sinter_boxes/",
				newSinterBoxes,
				sinterBoxes,
			);
		}
		async function setSinterBoxesAPI(newSinterBoxes){
			return await setAPI(
				"/api/nested_sinter_boxes/?reference_frame=nesting",
				newSinterBoxes,
				deleteRemovedSinterBoxes,
				setSinterBoxes
			);
		}
    async function setSinterBoxAPI(modifiedItem){
      return setSingleAPI("/api/nested_designs/", modifiedItem, sinterBoxes, setSinterBoxes);
    }
		return (
			<NestingConfiguration
				nesting={nesting}
				setNesting={setNestingAPI}
				items={items}
				setItems={setItemsAPI}
        setSingleItem={setItemAPI}
				nestingRuns={nestingRuns}
				setNestingRuns={setNestingRuns}
				currentRun={currentRun}
				_setCurrentRun={setCurrentRun}
				webSocket={webSocket}
				sinterBoxes={sinterBoxes}
				setSinterBoxes={setSinterBoxesAPI}
        setSingleSinterBoxItem={setSinterBoxAPI}
			/>
		)
	}else{
		return (
			<></>
		)
	}
}

export async function nestingLoader({params}){
	const request = await fetch("/api/groups/" + params.nestingSlug + "/");
	if(request.ok){
		return await request.json();
	}else{
		return null;	
	}
}

function NestingSubApp(){
  const loadedNesting = useLoaderData()
	const [nesting, setNesting] = useContext(NestingContext);
  useEffect(() => {
    setNesting(loadedNesting);
  }, [setNesting, loadedNesting]);
  localStorage.setItem("default_nesting", nesting.slug);
  if(nesting){
    return (
      <Nesting nesting={nesting} setNesting={setNesting} />
    )
  }else{
    return (
			<></>
		)
	}

}

export function NestingApp(){
  const landscape = useMediaQuery('(orientation: landscape)');
  if(landscape){
    return (
      <Box sx={{flexGrow: 1}}>
        <Grid container spacing={0} sx={{height: "100%"}}>
          <Grid item md={6} lg={9} sx={{height: "100%"}}>
            <Viewer/>
          </Grid>
          <Grid item md={6} lg={3} sx={{height: "100%"}}>
            <NestingSubApp/>
          </Grid>
        </Grid>
      </Box>
    )
  }else{
    return (
      <Box sx={{flexGrow: 1}}>
        <Stack sx={{height: "100%"}}>
            <Box sx={{height: "50%"}}> 
              <Viewer/>
            </Box>
            <Box sx={{height: "50%"}}> 
              <NestingSubApp/>
            </Box>
        </Stack>
      </Box>
    )
  }
}
